import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { IoCreateOutline, IoClose, IoAddOutline, IoTrashOutline } from "react-icons/io5";
import { useAuth } from "@/context/AuthContext";
import { getPageConfig, updatePageConfig } from "@/lib/api";
import ImageUpload from "@/components/ImageUpload";

const RED = "#e11d2a";
const genId = () => Math.random().toString(36).slice(2, 8);

// Révélation au scroll (IntersectionObserver) — inline, sans lib.
function Reveal({ children, delay = 0, style }) {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ob = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVis(true); ob.disconnect(); } }, { threshold: 0.12 });
    ob.observe(el);
    return () => ob.disconnect();
  }, []);
  return (
    <div ref={ref} style={{ ...style, opacity: vis ? 1 : 0, transform: vis ? "translateY(0)" : "translateY(34px)", transition: `opacity .9s cubic-bezier(.16,1,.3,1) ${delay}s, transform .9s cubic-bezier(.16,1,.3,1) ${delay}s` }}>
      {children}
    </div>
  );
}

/**
 * Landing publique — pilotée par un config éditable (page-config "landing").
 * Thème cinématographique sombre. L'admin édite Hero + blocs
 * (cards, text, pains, testimonials, founder, pricing, cta) via le panneau.
 */
export default function Landing() {
  const nav = useNavigate();
  const { isAuthenticated, user, isAdmin } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { document.title = "Tengoku Dōjō"; }, []);
  useEffect(() => { getPageConfig("landing").then(setCfg).catch(() => {}); }, []);

  const goLink = (link) => { if (!link) return; if (/^https?:\/\//.test(link)) window.open(link, "_blank"); else nav(link); };
  const openEdit = () => { setDraft(JSON.parse(JSON.stringify(cfg))); setEditing(true); };
  const saveEdit = async () => {
    setBusy(true);
    try { const saved = await updatePageConfig("landing", draft); setCfg(saved); setEditing(false); toast.success("Landing mise à jour"); }
    catch { toast.error("Échec de l'enregistrement"); } finally { setBusy(false); }
  };

  if (!cfg) return <div style={s.page} />;
  const hero = cfg.hero || {};
  const blocks = cfg.blocks || [];

  const renderBlock = (b) => {
    if (b.type === "cards") return (
      <section style={s.section} key={b.id}>
        <div style={s.container}>
          {b.title && <Reveal><h2 style={s.h2}>{b.title}</h2></Reveal>}
          <div style={s.cardsGrid}>
            {(b.items || []).map((sp, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <div style={s.card} className="ld-hover">
                  {sp.image && <div style={{ ...s.cardImg, backgroundImage: `url(${sp.image})` }} />}
                  {sp.kanji && <span style={s.cardKanji}>{sp.kanji}</span>}
                  {sp.emoji && !sp.kanji && <span style={s.cardEmoji}>{sp.emoji}</span>}
                  <div style={s.cardTitle}>{sp.title}</div>
                  <div style={s.cardDesc}>{sp.desc}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    );
    if (b.type === "text") return (
      <section style={{ ...s.section, borderTop: `1px solid ${RED}22` }} key={b.id} data-testid={b.id === "mission" ? "mission-text" : `landing-text-${b.id}`}>
        <div style={{ ...s.container, maxWidth: 880, textAlign: "center" }}>
          <Reveal>
            {b.image && <img src={b.image} alt="" style={s.textImg} />}
            {b.title && <h2 style={s.h2}>{b.title}</h2>}
            {b.body && (b.body.split("\n").filter((x) => x.trim()).map((p, i) => <p key={i} style={{ ...s.manifesto, marginBottom: 16 }}>{p}</p>))}
            {b.button?.label && <button style={{ ...s.ctaMain, marginTop: 26 }} className="ld-hover" onClick={() => goLink(b.button.link)}>{b.button.label}</button>}
          </Reveal>
        </div>
      </section>
    );
    if (b.type === "pains") return (
      <section style={s.section} key={b.id} data-testid="pains-section">
        <div style={s.container}>
          {b.title && <Reveal><h2 style={s.h2}>{b.title}</h2></Reveal>}
          {b.subtitle && <Reveal><p style={s.sectionSub}>{b.subtitle}</p></Reveal>}
          <div style={s.painsGrid}>
            {(b.items || []).map((it, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <div style={s.painCard} className="ld-pain" data-testid={`pain-card-${i + 1}`}>
                  <span style={s.painX} aria-hidden>✕</span>
                  {it.title && <div style={s.painTitle}>{it.title}</div>}
                  <div style={s.painText}>{it.text || it.desc}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    );
    if (b.type === "testimonials") return (
      <section style={s.section} key={b.id} data-testid="testimonials-section">
        <div style={s.container}>
          {b.title && <Reveal><h2 style={{ ...s.h2, textAlign: "center" }}>{b.title}</h2></Reveal>}
          <div style={s.testiGrid}>
            {(b.items || []).map((it, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <figure style={{ ...s.testiCard, marginTop: i % 2 ? 26 : 0 }} data-testid={`testimonial-${i + 1}`}>
                  <span style={s.testiQuote} aria-hidden>&ldquo;</span>
                  <blockquote style={s.testiText}>{it.quote || it.text}</blockquote>
                  <figcaption style={s.testiAuthor}>— {it.author}</figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    );
    if (b.type === "founder") return (
      <section style={s.section} key={b.id} data-testid="founder-bio-section">
        <div style={{ ...s.container, ...s.founderGrid }} className="ld-founder">
          <Reveal style={{ width: "100%" }}>
            <div style={s.founderImgWrap}>
              {b.image ? <img src={b.image} alt={b.name || "Fondateur"} style={s.founderImg} data-testid="founder-image" /> : <div style={{ ...s.founderImg, ...s.founderImgPlaceholder }}>天</div>}
              <div style={s.founderGlow} aria-hidden />
            </div>
          </Reveal>
          <Reveal delay={0.12} style={{ width: "100%" }}>
            <div data-testid="founder-text">
              {b.name && <h2 style={{ ...s.h2, marginBottom: 6 }}>{b.name}</h2>}
              {b.role && <div style={s.founderRole}>{b.role}</div>}
              {(b.body || "").split("\n").filter(Boolean).map((p, i) => <p key={i} style={s.founderBody}>{p}</p>)}
            </div>
          </Reveal>
        </div>
      </section>
    );
    if (b.type === "cta") return (
      <section style={{ ...s.section, paddingTop: 20 }} key={b.id}>
        <div style={s.container}>
          <Reveal>
            <div style={s.ctaBlock}>
              <div style={s.ctaKanji} aria-hidden>道</div>
              <div style={s.ctaBlockTitle}>{b.title}</div>
              {b.subtitle && <div style={s.ctaBlockSub}>{b.subtitle}</div>}
              {b.button?.label && <button style={s.ctaBlockBtn} className="ld-hover" onClick={() => goLink(isAuthenticated ? "/hub" : b.button.link)}>{isAuthenticated ? "Entrer dans le dōjō →" : b.button.label}</button>}
            </div>
          </Reveal>
        </div>
      </section>
    );
    if (b.type === "pricing") return (
      <section style={s.section} key={b.id}>
        <div style={{ ...s.container, display: "flex", justifyContent: "center" }}>
          <Reveal style={{ width: "100%", maxWidth: 460 }}>
            <div style={s.priceCard} className="ld-glow" data-testid="pricing-card">
              {b.tag && <div style={s.priceTag}>{b.tag}</div>}
              <div style={s.price}><span style={s.priceNum} data-testid="pricing-amount">{b.price}</span><span style={s.pricePer}>{b.per}</span></div>
              <ul style={s.features}>
                {(b.features || []).map((f, i) => <li key={i} style={s.featLi}><span style={s.featCheck}>✓</span> {f}</li>)}
              </ul>
              {b.button?.label && <button style={s.priceBtn} className="ld-pulse" data-testid="pricing-cta-button" onClick={() => goLink(isAuthenticated ? "/hub" : b.button.link)}>{isAuthenticated ? "Accéder au Hub" : b.button.label}</button>}
              {b.note && <p style={s.priceNote}>{b.note}</p>}
            </div>
          </Reveal>
        </div>
      </section>
    );
    return null;
  };

  return (
    <div style={s.page}>
      <header style={s.nav}>
        <div style={s.navInner}>
          <div style={s.brand} data-testid="nav-logo"><span style={s.brandKanji}>天</span><span style={s.brandText}>TENGOKU <span style={{ color: RED }}>DŌJŌ</span></span></div>
          <div style={s.navBtns}>
            {isAdmin && <button style={s.editBtn} data-testid="landing-edit" onClick={openEdit}><IoCreateOutline size={15} /> Modifier</button>}
            {isAuthenticated ? (
              <button style={s.enter} className="ld-hover" onClick={() => nav("/hub")}>Entrer dans le dōjō →</button>
            ) : (
              <>
                <button style={s.ghost} data-testid="nav-login-link" onClick={() => nav("/connexion")}>Se connecter</button>
                <button style={s.enter} className="ld-hover" data-testid="nav-join-button" onClick={() => nav("/inscription")}>Rejoindre</button>
              </>
            )}
          </div>
        </div>
      </header>

      <section style={s.hero} data-testid="landing-hero">
        {hero.bg && <div style={{ ...s.heroBg, backgroundImage: `url(${hero.bg})` }} aria-hidden />}
        <div style={s.heroOverlay} aria-hidden />
        {(hero.kanji ?? "道") && <div style={s.heroKanji} aria-hidden>{hero.kanji ?? "道"}</div>}
        <div style={s.heroContent}>
          <Reveal>{hero.badge && <div style={s.badge}>{hero.badge}</div>}</Reveal>
          <Reveal delay={0.08}><h1 style={s.h1} data-testid="hero-heading">{hero.title}</h1></Reveal>
          <Reveal delay={0.16}>{hero.subtitle && <p style={s.sub}>{hero.subtitle}</p>}</Reveal>
          <Reveal delay={0.24}>
            <div style={s.ctaRow}>
              {isAuthenticated ? (
                <button style={s.ctaMain} className="ld-hover" onClick={() => nav("/hub")}>Entrer dans le dōjō{user?.name ? `, ${user.name.split(" ")[0]}` : ""} →</button>
              ) : (
                (hero.buttons || []).map((btn, i) => (
                  <button key={i} data-testid={i === 0 ? "hero-cta-join" : `landing-hero-btn-${i}`} style={btn.variant === "ghost" ? s.ctaGhost : s.ctaMain} className="ld-hover" onClick={() => goLink(btn.link)}>{btn.label}</button>
                ))
              )}
            </div>
          </Reveal>
        </div>
        <div style={s.scrollHint} aria-hidden>↓</div>
      </section>

      {blocks.map(renderBlock)}

      <footer style={s.footer}>天 Tengoku Dōjō — {new Date().getFullYear()}</footer>

      {editing && draft && <LandingEditor draft={draft} setDraft={setDraft} onClose={() => setEditing(false)} onSave={saveEdit} busy={busy} />}
    </div>
  );
}

// ---------------- Éditeur ----------------
const BLOCK_LABELS = { cards: "Cartes", text: "Texte / Manifeste", pains: "Douleurs (❌)", testimonials: "Témoignages", founder: "Fondateur", pricing: "Tarif", cta: "Bannière CTA" };
const newBlock = (type) => {
  const id = genId();
  if (type === "cards") return { id, type, title: "", items: [{ kanji: "", emoji: "✨", title: "Titre", desc: "Description" }] };
  if (type === "text") return { id, type, title: "Nouveau titre", body: "Ton texte ici…", button: { label: "", link: "" } };
  if (type === "pains") return { id, type, title: "Ça te parle ?", subtitle: "", items: [{ title: "", text: "Décris la douleur…" }] };
  if (type === "testimonials") return { id, type, title: "Témoignages", items: [{ quote: "Un témoignage…", author: "@membre" }] };
  if (type === "founder") return { id, type, name: "Brichel Louzala", role: "Fondateur du Tengoku Dōjō", body: "Ton histoire…", image: "" };
  if (type === "cta") return { id, type, title: "Prêt·e à commencer ?", subtitle: "Rejoins le dōjō dès maintenant.", button: { label: "Rejoindre", link: "/inscription" } };
  if (type === "pricing") return { id, type, tag: "Accès", price: "19,99 €", per: "/ mois", features: ["Avantage 1"], button: { label: "Rejoindre", link: "/inscription" }, note: "Sans engagement." };
  return { id, type };
};

function LandingEditor({ draft, setDraft, onClose, onSave, busy }) {
  const hero = draft.hero || {};
  const blocks = draft.blocks || [];
  const setHero = (k, v) => setDraft({ ...draft, hero: { ...hero, [k]: v } });
  const setHeroBtn = (i, k, v) => { const l = [...(hero.buttons || [])]; l[i] = { ...l[i], [k]: v }; setHero("buttons", l); };
  const addHeroBtn = () => setHero("buttons", [...(hero.buttons || []), { label: "Bouton", link: "/inscription", variant: "primary" }]);
  const delHeroBtn = (i) => setHero("buttons", (hero.buttons || []).filter((_, x) => x !== i));

  const setBlocks = (l) => setDraft({ ...draft, blocks: l });
  const setBlock = (i, patch) => { const l = [...blocks]; l[i] = { ...l[i], ...patch }; setBlocks(l); };
  const moveBlock = (i, dir) => { const l = [...blocks]; const j = i + dir; if (j < 0 || j >= l.length) return; [l[i], l[j]] = [l[j], l[i]]; setBlocks(l); };
  const delBlock = (i) => setBlocks(blocks.filter((_, x) => x !== i));
  const addBlock = (type) => setBlocks([...blocks, newBlock(type)]);

  return (
    <div style={ed.overlay} onClick={onClose}>
      <div style={ed.modal} onClick={(e) => e.stopPropagation()} data-testid="landing-edit-modal">
        <div style={ed.head}>
          <span style={ed.title}>Modifier la Landing Page</span>
          <button style={ed.x} onClick={onClose} data-testid="landing-edit-close"><IoClose size={20} /></button>
        </div>
        <div style={ed.body}>
          <div style={ed.dividerRow}><span style={ed.dividerLabel}>Hero (haut de page)</span></div>
          <ImageUpload label="Image de fond du Hero" testid="landing-hero-bg" value={hero.bg || ""} onChange={(v) => setHero("bg", v)} />
          <label style={ed.lbl}>Kanji décoratif en fond (laisse vide pour l'enlever)</label>
          <input style={ed.input} data-testid="landing-hero-kanji" value={hero.kanji ?? "道"} onChange={(e) => setHero("kanji", e.target.value)} placeholder="道 (vide = aucun)" />
          <label style={ed.lbl}>Badge</label>
          <input style={ed.input} data-testid="landing-hero-badge" value={hero.badge || ""} onChange={(e) => setHero("badge", e.target.value)} />
          <label style={ed.lbl}>Titre</label>
          <textarea style={{ ...ed.input, resize: "vertical" }} rows={2} data-testid="landing-hero-title" value={hero.title || ""} onChange={(e) => setHero("title", e.target.value)} />
          <label style={ed.lbl}>Sous-titre</label>
          <textarea style={{ ...ed.input, resize: "vertical" }} rows={2} data-testid="landing-hero-subtitle" value={hero.subtitle || ""} onChange={(e) => setHero("subtitle", e.target.value)} />
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8 }}>
            <span style={ed.subLabel}>Boutons du Hero</span>
            <button style={ed.addBtn} data-testid="landing-hero-addbtn" onClick={addHeroBtn}><IoAddOutline size={14} /> Bouton</button>
          </div>
          {(hero.buttons || []).map((b, i) => (
            <div key={i} style={ed.itemRow}>
              <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8 }}>
                <input style={ed.input} placeholder="Libellé" value={b.label || ""} onChange={(e) => setHeroBtn(i, "label", e.target.value)} />
                <input style={ed.input} placeholder="Lien (interne /inscription ou https://…)" value={b.link || ""} onChange={(e) => setHeroBtn(i, "link", e.target.value)} />
                <select style={ed.input} value={b.variant || "primary"} onChange={(e) => setHeroBtn(i, "variant", e.target.value)}>
                  <option value="primary">Bouton plein (rouge)</option>
                  <option value="ghost">Bouton contour</option>
                </select>
              </div>
              <button style={ed.delBtn} onClick={() => delHeroBtn(i)}><IoTrashOutline size={15} /></button>
            </div>
          ))}

          <div style={ed.dividerRow}><span style={ed.dividerLabel}>Sections de la page</span></div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }} data-testid="landing-block-palette">
            {Object.keys(BLOCK_LABELS).map((t) => (
              <button key={t} style={ed.addBtn} data-testid={`landing-add-${t}`} onClick={() => addBlock(t)}><IoAddOutline size={14} /> {BLOCK_LABELS[t]}</button>
            ))}
          </div>
          {blocks.map((b, i) => (
            <div key={b.id} style={ed.blockCard} data-testid={`landing-block-${b.id}`}>
              <div style={ed.blockHead}>
                <span style={ed.blockName}>{BLOCK_LABELS[b.type] || b.type}</span>
                <div style={{ display: "flex", gap: 6 }}>
                  <button style={{ ...ed.iconBtn, opacity: i === 0 ? 0.4 : 1 }} disabled={i === 0} onClick={() => moveBlock(i, -1)} data-testid={`landing-block-up-${b.id}`}>↑</button>
                  <button style={{ ...ed.iconBtn, opacity: i === blocks.length - 1 ? 0.4 : 1 }} disabled={i === blocks.length - 1} onClick={() => moveBlock(i, 1)} data-testid={`landing-block-down-${b.id}`}>↓</button>
                  <button style={ed.iconBtnDel} onClick={() => delBlock(i)} data-testid={`landing-block-del-${b.id}`}><IoTrashOutline size={14} /></button>
                </div>
              </div>
              <BlockEditor block={b} onChange={(patch) => setBlock(i, patch)} />
            </div>
          ))}
        </div>
        <div style={ed.actions}>
          <button style={ed.cancel} onClick={onClose}>Annuler</button>
          <button style={{ ...ed.save, opacity: busy ? 0.6 : 1 }} disabled={busy} data-testid="landing-save" onClick={onSave}>{busy ? "Enregistrement…" : "Enregistrer"}</button>
        </div>
      </div>
    </div>
  );
}

function BlockEditor({ block, onChange }) {
  const b = block;
  const setBtn = (k, v) => onChange({ button: { ...(b.button || {}), [k]: v } });
  if (b.type === "cards") {
    const setItem = (i, k, v) => { const l = [...(b.items || [])]; l[i] = { ...l[i], [k]: v }; onChange({ items: l }); };
    const addItem = () => onChange({ items: [...(b.items || []), { kanji: "", emoji: "✨", title: "Titre", desc: "" }] });
    const delItem = (i) => onChange({ items: (b.items || []).filter((_, x) => x !== i) });
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <input style={ed.input} placeholder="Titre de la section (optionnel)" value={b.title || ""} onChange={(e) => onChange({ title: e.target.value })} />
        {(b.items || []).map((it, i) => (
          <div key={i} style={ed.itemRow}>
            <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8 }}>
              <div style={{ display: "flex", gap: 8 }}>
                <input style={{ ...ed.input, width: 70 }} placeholder="漢" value={it.kanji || ""} onChange={(e) => setItem(i, "kanji", e.target.value)} />
                <input style={{ ...ed.input, width: 70 }} placeholder="✨" value={it.emoji || ""} onChange={(e) => setItem(i, "emoji", e.target.value)} />
                <input style={{ ...ed.input, flex: 1 }} placeholder="Titre" value={it.title || ""} onChange={(e) => setItem(i, "title", e.target.value)} />
              </div>
              <input style={ed.input} placeholder="Description" value={it.desc || ""} onChange={(e) => setItem(i, "desc", e.target.value)} />
              <ImageUpload label="Image de la carte (optionnel)" testid={`landing-card-img-${i}`} value={it.image || ""} onChange={(v) => setItem(i, "image", v)} />
            </div>
            <button style={ed.delBtn} onClick={() => delItem(i)}><IoTrashOutline size={15} /></button>
          </div>
        ))}
        <button style={ed.addBtn} onClick={addItem}><IoAddOutline size={14} /> Ajouter une carte</button>
      </div>
    );
  }
  if (b.type === "text") return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <input style={ed.input} placeholder="Titre" value={b.title || ""} onChange={(e) => onChange({ title: e.target.value })} />
      <textarea style={{ ...ed.input, resize: "vertical" }} rows={4} placeholder="Paragraphe" value={b.body || ""} onChange={(e) => onChange({ body: e.target.value })} />
      <ImageUpload label="Image (optionnel)" testid="landing-text-img" value={b.image || ""} onChange={(v) => onChange({ image: v })} />
      <div style={{ display: "flex", gap: 8 }}>
        <input style={{ ...ed.input, flex: 1 }} placeholder="Libellé bouton (optionnel)" value={b.button?.label || ""} onChange={(e) => setBtn("label", e.target.value)} />
        <input style={{ ...ed.input, flex: 1 }} placeholder="Lien" value={b.button?.link || ""} onChange={(e) => setBtn("link", e.target.value)} />
      </div>
    </div>
  );
  if (b.type === "pains") {
    const setItem = (i, k, v) => { const l = [...(b.items || [])]; l[i] = { ...l[i], [k]: v }; onChange({ items: l }); };
    const addItem = () => onChange({ items: [...(b.items || []), { title: "", text: "" }] });
    const delItem = (i) => onChange({ items: (b.items || []).filter((_, x) => x !== i) });
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <input style={ed.input} placeholder="Titre de la section" value={b.title || ""} onChange={(e) => onChange({ title: e.target.value })} />
        <input style={ed.input} placeholder="Sous-titre (optionnel)" value={b.subtitle || ""} onChange={(e) => onChange({ subtitle: e.target.value })} />
        {(b.items || []).map((it, i) => (
          <div key={i} style={ed.itemRow}>
            <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8 }}>
              <input style={ed.input} placeholder="Titre court (optionnel)" value={it.title || ""} onChange={(e) => setItem(i, "title", e.target.value)} />
              <textarea style={{ ...ed.input, resize: "vertical" }} rows={2} placeholder="La douleur…" value={it.text || ""} onChange={(e) => setItem(i, "text", e.target.value)} />
            </div>
            <button style={ed.delBtn} onClick={() => delItem(i)}><IoTrashOutline size={15} /></button>
          </div>
        ))}
        <button style={ed.addBtn} onClick={addItem}><IoAddOutline size={14} /> Ajouter une douleur</button>
      </div>
    );
  }
  if (b.type === "testimonials") {
    const setItem = (i, k, v) => { const l = [...(b.items || [])]; l[i] = { ...l[i], [k]: v }; onChange({ items: l }); };
    const addItem = () => onChange({ items: [...(b.items || []), { quote: "", author: "" }] });
    const delItem = (i) => onChange({ items: (b.items || []).filter((_, x) => x !== i) });
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <input style={ed.input} placeholder="Titre de la section" value={b.title || ""} onChange={(e) => onChange({ title: e.target.value })} />
        {(b.items || []).map((it, i) => (
          <div key={i} style={ed.itemRow}>
            <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8 }}>
              <textarea style={{ ...ed.input, resize: "vertical" }} rows={3} placeholder="Le témoignage…" value={it.quote || ""} onChange={(e) => setItem(i, "quote", e.target.value)} />
              <input style={ed.input} placeholder="Auteur (ex: @membre)" value={it.author || ""} onChange={(e) => setItem(i, "author", e.target.value)} />
            </div>
            <button style={ed.delBtn} onClick={() => delItem(i)}><IoTrashOutline size={15} /></button>
          </div>
        ))}
        <button style={ed.addBtn} onClick={addItem}><IoAddOutline size={14} /> Ajouter un témoignage</button>
      </div>
    );
  }
  if (b.type === "founder") return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <input style={ed.input} placeholder="Nom" value={b.name || ""} onChange={(e) => onChange({ name: e.target.value })} />
      <input style={ed.input} placeholder="Rôle (ex: Fondateur du Tengoku Dōjō)" value={b.role || ""} onChange={(e) => onChange({ role: e.target.value })} />
      <textarea style={{ ...ed.input, resize: "vertical" }} rows={5} placeholder="Ta présentation (un paragraphe par ligne)" value={b.body || ""} onChange={(e) => onChange({ body: e.target.value })} />
      <ImageUpload label="Ta photo" testid="landing-founder-img" value={b.image || ""} onChange={(v) => onChange({ image: v })} />
    </div>
  );
  if (b.type === "cta") return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <input style={ed.input} placeholder="Titre" value={b.title || ""} onChange={(e) => onChange({ title: e.target.value })} />
      <input style={ed.input} placeholder="Sous-titre" value={b.subtitle || ""} onChange={(e) => onChange({ subtitle: e.target.value })} />
      <div style={{ display: "flex", gap: 8 }}>
        <input style={{ ...ed.input, flex: 1 }} placeholder="Libellé bouton" value={b.button?.label || ""} onChange={(e) => setBtn("label", e.target.value)} />
        <input style={{ ...ed.input, flex: 1 }} placeholder="Lien" value={b.button?.link || ""} onChange={(e) => setBtn("link", e.target.value)} />
      </div>
    </div>
  );
  if (b.type === "pricing") {
    const setFeatures = (v) => onChange({ features: v.split("\n").map((x) => x.trim()).filter(Boolean) });
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <input style={ed.input} placeholder="Étiquette (ex: Accès complet)" value={b.tag || ""} onChange={(e) => onChange({ tag: e.target.value })} />
        <div style={{ display: "flex", gap: 8 }}>
          <input style={{ ...ed.input, flex: 1 }} placeholder="Prix (ex: 19,99 €)" value={b.price || ""} onChange={(e) => onChange({ price: e.target.value })} />
          <input style={{ ...ed.input, width: 110 }} placeholder="/ mois" value={b.per || ""} onChange={(e) => onChange({ per: e.target.value })} />
        </div>
        <textarea style={{ ...ed.input, resize: "vertical" }} rows={4} placeholder="Un avantage par ligne" value={(b.features || []).join("\n")} onChange={(e) => setFeatures(e.target.value)} />
        <div style={{ display: "flex", gap: 8 }}>
          <input style={{ ...ed.input, flex: 1 }} placeholder="Libellé bouton" value={b.button?.label || ""} onChange={(e) => setBtn("label", e.target.value)} />
          <input style={{ ...ed.input, flex: 1 }} placeholder="Lien" value={b.button?.link || ""} onChange={(e) => setBtn("link", e.target.value)} />
        </div>
        <input style={ed.input} placeholder="Note sous le bouton" value={b.note || ""} onChange={(e) => onChange({ note: e.target.value })} />
      </div>
    );
  }
  return null;
}

const HFONT = "'Oswald', 'Outfit', system-ui, sans-serif";
const s = {
  page: { minHeight: "100vh", background: "#050505", color: "#FAFAFA", fontFamily: "'Outfit', system-ui, sans-serif", position: "relative", overflowX: "hidden" },
  nav: { position: "sticky", top: 0, zIndex: 50, background: "rgba(5,5,5,0.72)", backdropFilter: "blur(16px)", borderBottom: "1px solid rgba(255,255,255,0.08)" },
  navInner: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 24px", maxWidth: 1200, margin: "0 auto" },
  brand: { display: "flex", alignItems: "center", gap: 10 },
  brandKanji: { fontSize: 26, fontWeight: 700, color: "#fff" },
  brandText: { fontSize: 16, fontWeight: 800, letterSpacing: 2, fontFamily: HFONT },
  navBtns: { display: "flex", gap: 10, alignItems: "center" },
  editBtn: { display: "inline-flex", alignItems: "center", gap: 5, padding: "9px 14px", borderRadius: 10, border: `1.5px solid ${RED}`, background: "transparent", color: RED, fontWeight: 800, cursor: "pointer", fontSize: 13 },
  ghost: { padding: "10px 18px", borderRadius: 10, border: "1.5px solid rgba(255,255,255,0.18)", background: "transparent", color: "#eee", fontWeight: 600, cursor: "pointer", fontSize: 14 },
  enter: { padding: "10px 20px", borderRadius: 10, border: "none", background: RED, color: "#fff", fontWeight: 700, cursor: "pointer", fontSize: 14, boxShadow: `0 6px 22px ${RED}66` },

  hero: { position: "relative", minHeight: "92vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center", overflow: "hidden", padding: "40px 24px" },
  heroBg: { position: "absolute", inset: 0, backgroundSize: "cover", backgroundPosition: "center", filter: "grayscale(0.2) contrast(1.05)", animation: "ldZoom 22s ease-out forwards" },
  heroOverlay: { position: "absolute", inset: 0, background: "linear-gradient(to bottom, rgba(5,5,5,0.66), rgba(5,5,5,0.86) 55%, #050505)" },
  heroKanji: { position: "absolute", right: "-4vw", top: "8%", fontSize: "46vh", color: RED, opacity: 0.06, fontWeight: 800, lineHeight: 1, pointerEvents: "none", userSelect: "none", fontFamily: HFONT },
  heroContent: { position: "relative", zIndex: 2, maxWidth: 860 },
  badge: { display: "inline-block", padding: "7px 16px", borderRadius: 999, background: `${RED}1f`, color: "#ff6b74", fontWeight: 700, fontSize: 12.5, marginBottom: 24, border: `1px solid ${RED}55`, letterSpacing: 1, textTransform: "uppercase" },
  h1: { fontSize: "clamp(40px,7.5vw,84px)", fontWeight: 800, lineHeight: 0.98, margin: "0 0 22px", letterSpacing: "-0.02em", textTransform: "uppercase", fontFamily: HFONT, textShadow: "0 8px 40px rgba(0,0,0,0.6)" },
  sub: { fontSize: "clamp(16px,2vw,20px)", color: "#c9c9d0", lineHeight: 1.6, maxWidth: 640, margin: "0 auto 34px" },
  ctaRow: { display: "flex", gap: 14, justifyContent: "center", flexWrap: "wrap" },
  ctaMain: { padding: "16px 34px", borderRadius: 12, border: "none", background: RED, color: "#fff", fontWeight: 800, fontSize: 16, cursor: "pointer", boxShadow: `0 12px 34px ${RED}66`, textTransform: "uppercase", letterSpacing: 0.5, fontFamily: HFONT },
  ctaGhost: { padding: "16px 30px", borderRadius: 12, border: "1.5px solid rgba(255,255,255,0.25)", background: "rgba(255,255,255,0.04)", color: "#fff", fontWeight: 700, fontSize: 16, cursor: "pointer", textTransform: "uppercase", letterSpacing: 0.5, fontFamily: HFONT },
  scrollHint: { position: "absolute", bottom: 22, left: "50%", transform: "translateX(-50%)", color: "rgba(255,255,255,0.4)", fontSize: 22, animation: "ldBounce 1.8s ease-in-out infinite", zIndex: 2 },

  section: { position: "relative", zIndex: 2, padding: "clamp(56px,9vw,120px) 0" },
  container: { maxWidth: 1160, margin: "0 auto", padding: "0 24px" },
  h2: { fontSize: "clamp(28px,4.2vw,50px)", fontWeight: 700, margin: "0 0 14px", letterSpacing: "-0.01em", textTransform: "uppercase", fontFamily: HFONT, lineHeight: 1.05 },
  sectionSub: { fontSize: 16.5, color: "#9a9aa2", margin: "0 0 34px", maxWidth: 640 },
  manifesto: { fontSize: "clamp(17px,2.1vw,22px)", color: "#c7c7cf", lineHeight: 1.75, fontWeight: 300, margin: 0 },
  textImg: { width: "100%", maxWidth: 640, borderRadius: 16, margin: "0 auto 26px", display: "block", objectFit: "cover", border: "1px solid #27272A" },

  cardsGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(240px,1fr))", gap: 18, marginTop: 30 },
  card: { position: "relative", overflow: "hidden", padding: "28px 24px", borderRadius: 18, border: "1px solid #27272A", background: "#121212", height: "100%" },
  cardImg: { width: "100%", height: 150, borderRadius: 12, backgroundSize: "cover", backgroundPosition: "center", marginBottom: 16 },
  cardKanji: { position: "absolute", right: 6, top: -22, fontSize: 120, fontWeight: 800, color: "rgba(225,29,42,0.07)", fontFamily: HFONT },
  cardEmoji: { fontSize: 30 },
  cardTitle: { fontSize: 21, fontWeight: 700, marginTop: 8, fontFamily: HFONT, textTransform: "uppercase", letterSpacing: 0.3 },
  cardDesc: { fontSize: 14.5, color: "#9a9aa2", marginTop: 8, lineHeight: 1.6, position: "relative" },

  painsGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))", gap: 18, marginTop: 30 },
  painCard: { position: "relative", padding: "30px 26px 28px", borderRadius: 18, border: "1px solid #27272A", background: "#0d0d0d", height: "100%" },
  painX: { display: "inline-flex", alignItems: "center", justifyContent: "center", width: 46, height: 46, borderRadius: 12, background: `${RED}1a`, color: RED, fontSize: 24, fontWeight: 900, marginBottom: 16, boxShadow: `inset 0 0 0 1px ${RED}44` },
  painTitle: { fontSize: 18, fontWeight: 700, marginBottom: 8, fontFamily: HFONT, color: "#fff" },
  painText: { fontSize: 15, color: "#b7b7bf", lineHeight: 1.65 },

  testiGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))", gap: 20, marginTop: 34, alignItems: "start" },
  testiCard: { position: "relative", overflow: "hidden", padding: "30px 26px", borderRadius: 18, background: "rgba(255,255,255,0.04)", backdropFilter: "blur(10px)", border: "1px solid rgba(255,255,255,0.1)", margin: 0 },
  testiQuote: { position: "absolute", top: -24, left: 10, fontSize: 130, color: `${RED}22`, fontFamily: "Georgia, serif", lineHeight: 1, pointerEvents: "none" },
  testiText: { fontSize: 15.5, fontStyle: "italic", color: "#e6e6ea", lineHeight: 1.7, margin: "0 0 16px", position: "relative", fontWeight: 300 },
  testiAuthor: { fontSize: 14, fontWeight: 800, color: RED, fontFamily: HFONT, letterSpacing: 0.4 },

  founderGrid: { display: "grid", alignItems: "center" },
  founderImgWrap: { position: "relative" },
  founderImg: { width: "100%", aspectRatio: "4/5", objectFit: "cover", borderRadius: 20, border: "1px solid #27272A", display: "block", position: "relative", zIndex: 2 },
  founderImgPlaceholder: { display: "flex", alignItems: "center", justifyContent: "center", fontSize: 120, color: `${RED}55`, background: "#101010", fontFamily: HFONT },
  founderGlow: { position: "absolute", inset: "-8% -8% 20% -8%", background: `radial-gradient(circle at 60% 40%, ${RED}44, transparent 65%)`, filter: "blur(30px)", zIndex: 1 },
  founderRole: { color: RED, fontWeight: 800, fontSize: 14, textTransform: "uppercase", letterSpacing: 1, marginBottom: 18, fontFamily: HFONT },
  founderBody: { fontSize: 16, color: "#bdbdc5", lineHeight: 1.8, margin: "0 0 14px" },

  ctaBlock: { position: "relative", overflow: "hidden", textAlign: "center", padding: "clamp(40px,6vw,64px) 28px", borderRadius: 26, background: `linear-gradient(135deg, ${RED}, #7a0d16)`, color: "#fff", boxShadow: `0 26px 65px ${RED}55` },
  ctaKanji: { position: "absolute", right: -20, bottom: -60, fontSize: 260, color: "rgba(255,255,255,0.08)", fontWeight: 800, lineHeight: 1, fontFamily: HFONT, pointerEvents: "none" },
  ctaBlockTitle: { fontSize: "clamp(26px,4vw,42px)", fontWeight: 800, margin: "0 0 10px", position: "relative", fontFamily: HFONT, textTransform: "uppercase" },
  ctaBlockSub: { fontSize: 17, opacity: 0.94, margin: "0 0 26px", position: "relative" },
  ctaBlockBtn: { padding: "15px 34px", borderRadius: 999, border: "none", background: "#fff", color: RED, fontWeight: 900, fontSize: 16, cursor: "pointer", position: "relative", fontFamily: HFONT, textTransform: "uppercase", letterSpacing: 0.5 },

  priceCard: { width: "100%", padding: 36, borderRadius: 24, border: `1px solid ${RED}66`, background: "linear-gradient(to bottom, #1A1A1A, #0A0A0A)", boxShadow: `0 24px 60px rgba(0,0,0,0.6), 0 0 0 1px ${RED}22, 0 0 60px ${RED}22`, textAlign: "center" },
  priceTag: { color: RED, fontWeight: 800, fontSize: 13, textTransform: "uppercase", letterSpacing: 1.5, fontFamily: HFONT },
  price: { margin: "12px 0 20px" },
  priceNum: { fontSize: 52, fontWeight: 800, fontFamily: HFONT, color: "#fff" },
  pricePer: { fontSize: 16, color: "#9a9aa2", marginLeft: 6 },
  features: { listStyle: "none", padding: 0, margin: "0 0 26px", textAlign: "left", display: "flex", flexDirection: "column", gap: 12, color: "#dcdce2", fontSize: 15 },
  featLi: { display: "flex", alignItems: "center", gap: 10 },
  featCheck: { color: RED, fontWeight: 900, flexShrink: 0 },
  priceBtn: { width: "100%", padding: "16px 0", borderRadius: 12, border: "none", background: RED, color: "#fff", fontWeight: 800, fontSize: 17, cursor: "pointer", boxShadow: `0 12px 34px ${RED}66`, textTransform: "uppercase", letterSpacing: 0.5, fontFamily: HFONT },
  priceNote: { fontSize: 12.5, color: "#7a7a82", marginTop: 14 },

  footer: { position: "relative", zIndex: 2, textAlign: "center", padding: "28px", color: "#5a5a62", fontSize: 13, borderTop: "1px solid #1a1a1a" },
};

const ed = {
  overlay: { position: "fixed", inset: 0, zIndex: 10000, background: "rgba(0,0,0,0.65)", backdropFilter: "blur(3px)", display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "32px 16px", overflowY: "auto", color: "#111" },
  modal: { width: "100%", maxWidth: 640, background: "#fff", borderRadius: 18, boxShadow: "0 30px 80px rgba(0,0,0,0.5)", overflow: "hidden" },
  head: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 20px", borderBottom: "1px solid #eee" },
  title: { fontSize: 16.5, fontWeight: 900 },
  x: { width: 34, height: 34, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 8, border: "1px solid #e5e5e5", background: "#fafafa", cursor: "pointer", color: "#666" },
  body: { padding: 20, display: "flex", flexDirection: "column", gap: 8, maxHeight: "70vh", overflowY: "auto" },
  lbl: { fontSize: 12.5, fontWeight: 700, color: "#888", marginTop: 6 },
  subLabel: { fontSize: 12.5, fontWeight: 800, color: "#444" },
  input: { width: "100%", padding: "10px 12px", border: "1.5px solid #e2e2e2", borderRadius: 10, fontSize: 14, fontFamily: "inherit", color: "#111", background: "#fff", boxSizing: "border-box" },
  dividerRow: { display: "flex", alignItems: "center", justifyContent: "space-between", margin: "16px 0 6px", paddingTop: 14, borderTop: "1px solid #eee" },
  dividerLabel: { fontSize: 13, fontWeight: 900, color: RED, textTransform: "uppercase", letterSpacing: 1 },
  addBtn: { display: "inline-flex", alignItems: "center", gap: 4, padding: "7px 12px", borderRadius: 8, border: `1.5px solid ${RED}`, background: "#fff", color: RED, fontSize: 12.5, fontWeight: 800, cursor: "pointer" },
  itemRow: { display: "flex", gap: 8, padding: 12, borderRadius: 12, border: "1px solid #eee", background: "#fafafa", marginBottom: 2 },
  delBtn: { width: 34, height: 34, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 8, border: "1px solid #e5e5e5", background: "#fff", color: RED, cursor: "pointer" },
  blockCard: { border: "1px solid #eee", borderRadius: 14, padding: 12, background: "#fff", marginBottom: 8 },
  blockHead: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 },
  blockName: { fontSize: 13.5, fontWeight: 900, color: "#111" },
  iconBtn: { width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 8, border: "1px solid #e5e5e5", background: "#fafafa", color: "#111", fontWeight: 800, cursor: "pointer", fontSize: 15 },
  iconBtnDel: { width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 8, border: "1px solid #e5e5e5", background: "#fff", color: RED, cursor: "pointer" },
  actions: { display: "flex", justifyContent: "flex-end", gap: 10, padding: "14px 20px", borderTop: "1px solid #eee" },
  cancel: { padding: "11px 18px", borderRadius: 10, border: "1.5px solid #e2e2e2", background: "#fff", color: "#666", fontWeight: 700, cursor: "pointer" },
  save: { padding: "11px 22px", borderRadius: 10, border: "none", background: RED, color: "#fff", fontWeight: 800, cursor: "pointer" },
};

// Fonts + keyframes + micro-interactions (injectés une fois)
if (typeof document !== "undefined" && !document.getElementById("ld-assets")) {
  const link = document.createElement("link");
  link.id = "ld-fonts"; link.rel = "stylesheet";
  link.href = "https://fonts.googleapis.com/css2?family=Oswald:wght@500;600;700&family=Outfit:wght@300;400;600;700;800;900&display=swap";
  document.head.appendChild(link);
  const st = document.createElement("style");
  st.id = "ld-assets";
  st.textContent = `
    @keyframes ldZoom { from { transform: scale(1.12); } to { transform: scale(1); } }
    @keyframes ldBounce { 0%,100%{ transform: translate(-50%,0);} 50%{ transform: translate(-50%,8px);} }
    @keyframes ldPulse { 0%,100%{ box-shadow: 0 12px 34px ${RED}66; } 50%{ box-shadow: 0 12px 44px ${RED}aa; } }
    .ld-hover { transition: transform .25s ease, box-shadow .25s ease, border-color .25s ease; }
    .ld-hover:hover { transform: translateY(-3px); box-shadow: 0 10px 34px ${RED}55; }
    .ld-pain { transition: transform .25s ease, border-color .25s ease; }
    .ld-pain:hover { transform: translateY(-3px); border-color: ${RED}88; }
    .ld-glow { transition: box-shadow .4s ease; }
    .ld-pulse { animation: ldPulse 2.4s ease-in-out infinite; transition: transform .2s ease; }
    .ld-pulse:hover { transform: translateY(-2px); }
    .ld-founder { grid-template-columns: minmax(0,1fr) minmax(0,1.2fr); gap: clamp(28px,5vw,64px); }
    @media (max-width: 820px) {
      .ld-founder { grid-template-columns: 1fr; gap: 28px; }
      .ld-founder img { max-width: 320px; margin-left: auto; margin-right: auto; }
    }
  `;
  document.head.appendChild(st);
}
